import { SearchSort } from '@aipb/castor/model';
import { AppConfig } from '@aipb/env/util';
import { isUndefined } from '@aipb/util/other';
import {
  FormSettingsModel,
  NewsItem,
  PreparedQueryModel,
  ResultListMode,
  SearchFormMode,
  SearchSettingsModel,
  Topic
} from '../model';
import {
  CatalogCastorConfig,
  CatalogEnv,
  CatalogFormat,
  CatalogFulltextConfig,
  CatalogTranslationEnv,
  MapSearch,
  PreparedQueryMode,
  PreparedQuerySimple,
  ReportHighlightMode
} from './catalog.env';

export class CatalogConfig extends AppConfig<CatalogEnv> {
  get castor(): CatalogCastorConfig {
    // @ts-expect-error foo
    return this.config.castor;
  }

  get enableResultListMode(): boolean {
    // @ts-expect-error foo
    return isUndefined(this.config.enableResultListMode) ? true : this.config.enableResultListMode;
  }

  get defaultResultListMode(): ResultListMode {
    return this.config.defaultResultListMode || 'basic';
  }

  get resultListModes(): ResultListMode[] {
    return this.config.resultListModes || ['basic', 'basic-compact', 'tile', 'masonry', 'tile-single-column'];
  }

  get defaultLockDetailTabs(): boolean {
    return this.config.defaultLockDetailTabs || false;
  }

  get defaultLockResultTabs(): boolean {
    return this.config.defaultLockResultTabs || false;
  }

  get enableHighlighting(): boolean {
    return this.config.enableHighlighting || false;
  }

  get searchSettings(): string[] {
    return this.config.searchSettings || ['asterLeft', 'asterRight', 'asciiSearch'];
  }

  get headerGulliverUrl() {
    return this.config.headerGulliverUrl || undefined;
  }

  get defaultSearchSettings(): SearchSettingsModel {
    return (
      this.config.defaultSearchSettings || {
        asciiSearch: false,
        asterLeft: false,
        asterRight: false
      }
    );
  }

  get formSettings(): FormSettingsModel {
    return (
      this.config.formSettings || {
        distanceSearch: false
      }
    );
  }

  get defaultFormSettings(): FormSettingsModel {
    return this.config.defaultFormSettings || {};
  }

  get showFilters(): boolean {
    return this.config.showFilters || false;
  }

  get news(): NewsItem[] {
    return this.config.news || [];
  }

  get topics(): Topic[] {
    return this.config.topics || [];
  }

  get mapSearch(): MapSearch {
    // @ts-expect-error foo
    return this.config.mapSearch;
  }

  get fulltext(): CatalogFulltextConfig {
    // @ts-expect-error foo
    return this.config.fulltext;
  }

  get searchTabMaxLength(): number {
    return this.config.searchTabMaxLength || 30;
  }

  get headerFormat(): CatalogFormat {
    return this.config.headerFormat || 'xml';
  }

  get headerCollapsible(): boolean {
    return this.config.headerCollapsible || false;
  }

  get fetchDetailFormat(): CatalogFormat {
    return this.config.fetchDetailFormat || 'json';
  }

  get maxBrowserDetailImageWidth(): number {
    return this.config.maxBrowserDetailImageWidth || 1000;
  }

  get maxBrowserDetailImageHeight(): number {
    return this.config.maxBrowserDetailImageHeight || 1000;
  }

  get sort(): SearchSort[] {
    return this.config.sort || [];
  }

  get castorHeaderImageHeight(): number {
    return this.config.castorHeaderImageHeight || 100;
  }

  get fetchDetailApiUrl(): string {
    // @ts-expect-error foo
    return this.config.fetchDetailApiUrl;
  }

  get similarRecordApiUrl(): string {
    // @ts-expect-error foo
    return this.config.similarRecordApiUrl;
  }

  get useLightboxForHeader(): boolean {
    return this.config.useLightboxForHeader || false;
  }

  get preparedQueries(): PreparedQueryModel[] {
    return this.config.preparedQueries || [];
  }

  get preparedQueriesTranslated(): Record<string, PreparedQueryModel[]> {
    return this.config.preparedQueriesTranslated || {};
  }

  get translatableFacets(): string[] {
    return this.config.translatableFacets || [];
  }

  get showHeaderImageIcon(): boolean {
    return this.config.showHeaderImageIcon || false;
  }

  get truncateHeaderLabel(): number {
    return this.config.truncateHeaderLabel || 250;
  }

  get hideSortInHeader(): boolean {
    return this.config.hideSortInHeader || false;
  }

  get enableFirstSearch(): boolean {
    // @ts-expect-error foo
    return isUndefined(this.config.enableFirstSearch) ? true : this.config.enableFirstSearch;
  }

  get reportHighlightMode(): ReportHighlightMode {
    return this.config.reportHighlightMode || 'all_fields';
  }

  get services() {
    return this.config.services;
  }

  get cs(): CatalogTranslationEnv {
    // @ts-expect-error foo
    return this.config.cs;
  }

  get en(): CatalogTranslationEnv {
    // @ts-expect-error foo
    return this.config.en;
  }

  get de(): CatalogTranslationEnv {
    // @ts-expect-error foo
    return this.config.de;
  }

  get searchFormMode(): SearchFormMode {
    return this.config.searchFormMode || 'simple';
  }

  get preparedQueriesMode(): PreparedQueryMode {
    return this.config.preparedQueriesMode || 'default';
  }

  get preparedQueriesSimple(): PreparedQuerySimple {
    return (
      this.config.preparedQueriesSimple || {
        fromColor: 'rgba(0, 0, 0, 0.0)',
        toColor: 'rgba(0, 0, 0, 0.6)'
      }
    );
  }

  get filterSidenavWidth() {
    return this.config.filterSidenavWidth || null;
  }

  get showRegisterStatistics() {
    return this.config.showRegisterStatistics || false;
  }

  get lightboxEnabled() {
    return this.config.lightboxEnabled || false;
  }

  get facetConfigurations() {
    return (
      this.config.facetConfigurations || [
        {
          type: 'list'
        }
      ]
    );
  }

  get virtualKeyboard() {
    return this.config.virtualKeyboard || null;
  }

  getPreparedQueriesTranslated(lang: string): PreparedQueryModel[] {
    if (!this.config.preparedQueriesTranslated && !this.config.preparedQueries) return [];

    if (this.config.preparedQueriesTranslated && lang in this.config.preparedQueriesTranslated)
      return this.config.preparedQueriesTranslated[lang];

    if (this.config.preparedQueries) return this.config.preparedQueries;

    return [];
  }
}
